import React, { useState, useEffect } from 'react';
import { AuthProvider, useAuth } from './context/AuthContext';
import Sidebar, { PAGE_TITLES } from './components/Sidebar';
import Dashboard from './components/Dashboard';
import Analytics from './components/Analytics';
import ScraperInterface from './components/ScraperInterface';
import LeadCollector from './components/LeadCollector';
import RecordsBrowser from './components/RecordsBrowser';
import EmailCampaign from './components/EmailCampaign';
import InboxManager from './components/InboxManager';
import Followups from './components/Followups';
import LeadPipeline from './components/LeadPipeline';
import DemosTracker from './components/DemosTracker';
import Mailboxes from './components/Mailboxes';
import Settings from './components/Settings';
import LoginPage from './components/LoginPage';
import RegisterPage from './components/RegisterPage';
import { LogOut, ChevronDown, Sun, Moon, Menu } from 'lucide-react';
import './App.css';

/* ── Theme hook ─────────────────────────────────────────────── */
const useTheme = () => {
  const [dark, setDark] = useState(() => {
    const saved = localStorage.getItem('theme');
    if (saved) return saved === 'dark';
    return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;
  });

  useEffect(() => {
    const root = document.documentElement;
    if (dark) {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    localStorage.setItem('theme', dark ? 'dark' : 'light');
  }, [dark]);

  return [dark, () => setDark(d => !d)];
};

/* ── Responsive hook ────────────────────────────────────────── */
const useIsMobile = () => {
  const [mobile, setMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const handler = (e) => setMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return mobile;
};

const AuthenticatedApp = () => {
  const { user, logout } = useAuth();
  const [activePage, setActivePage] = useState(() => {
    const saved = localStorage.getItem('activePage');
    return saved && PAGE_TITLES[saved] ? saved : 'dashboard';
  });
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [dark, toggleTheme] = useTheme();
  const isMobile = useIsMobile();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('sidebarCollapsed') === '1');
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => { localStorage.setItem('activePage', activePage); }, [activePage]);
  useEffect(() => { localStorage.setItem('sidebarCollapsed', collapsed ? '1' : '0'); }, [collapsed]);
  useEffect(() => {
    const handler = (e) => {
      if (!e.target.closest('#user-menu-btn')) setUserMenuOpen(false);
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  const sidebarWidth = isMobile ? 0 : (collapsed ? 68 : 232);

  return (
    <div style={{ minHeight: '100vh', background: 'var(--c-bg)' }}>
      <Sidebar
        active={activePage}
        onSelect={setActivePage}
        collapsed={collapsed}
        onToggleCollapse={() => setCollapsed(c => !c)}
        isMobile={isMobile}
        mobileOpen={mobileOpen}
        onCloseMobile={() => setMobileOpen(false)}
      />

      <div style={{ marginLeft: sidebarWidth, transition: 'margin-left 0.2s ease', minWidth: 0 }}>
        {/* Top bar */}
        <header style={{
          position: 'sticky', top: 0, zIndex: 50,
          background: 'var(--c-nav-bg)',
          backdropFilter: 'blur(20px)',
          borderBottom: '1px solid var(--c-nav-border)',
          boxShadow: 'var(--shadow-nav)',
        }}>
          <div style={{ padding: '0 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 60 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {isMobile && (
                <button
                  onClick={() => setMobileOpen(true)}
                  style={{
                    width: 36, height: 36, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    border: '1px solid var(--c-border)', background: 'var(--c-surface)', color: 'var(--c-text-2)', cursor: 'pointer',
                  }}
                >
                  <Menu size={17} />
                </button>
              )}
              <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: 17, color: 'var(--c-text)' }}>
                {PAGE_TITLES[activePage] || 'WNLeads'}
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              {/* Theme Toggle */}
              <button
                onClick={toggleTheme}
                title={dark ? 'Switch to light mode' : 'Switch to dark mode'}
                style={{
                  width: 36, height: 36, borderRadius: 10,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  border: '1px solid var(--c-border)',
                  background: 'var(--c-surface)',
                  cursor: 'pointer', color: 'var(--c-text-2)',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--c-border-bright)'; e.currentTarget.style.color = 'var(--c-primary)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--c-border)'; e.currentTarget.style.color = 'var(--c-text-2)'; }}
              >
                {dark ? <Sun size={16} /> : <Moon size={16} />}
              </button>

              {/* User Menu */}
              <div style={{ position: 'relative' }}>
                <button
                  id="user-menu-btn"
                  onClick={() => setUserMenuOpen(o => !o)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '7px 12px', borderRadius: 12,
                    border: '1px solid var(--c-border)',
                    background: 'var(--c-surface)',
                    cursor: 'pointer', color: 'var(--c-text)',
                    fontFamily: "'Inter', sans-serif",
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--c-border-bright)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--c-border)'; }}
                >
                  <div style={{
                    width: 28, height: 28, borderRadius: '50%',
                    background: 'linear-gradient(135deg, var(--c-primary), var(--c-accent))',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 12, fontWeight: 700, color: 'var(--c-btn-text)',
                  }}>
                    {(user?.name || 'U').charAt(0).toUpperCase()}
                  </div>
                  {!isMobile && (
                    <div style={{ textAlign: 'left' }}>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{user?.name || 'Admin'}</div>
                      <div style={{ fontSize: 11, color: 'var(--c-text-3)' }}>{user?.email}</div>
                    </div>
                  )}
                  <ChevronDown size={14} style={{ color: 'var(--c-text-3)', transition: 'transform 0.2s', transform: userMenuOpen ? 'rotate(180deg)' : 'none' }} />
                </button>

                {userMenuOpen && (
                  <div style={{
                    position: 'absolute', top: 'calc(100% + 8px)', right: 0,
                    width: 200, background: 'var(--c-bg)',
                    border: '1px solid var(--c-border)',
                    borderRadius: 12, boxShadow: 'var(--shadow-elevated)',
                    overflow: 'hidden', zIndex: 200,
                    animation: 'scaleIn 0.15s ease',
                  }}>
                    <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--c-border)' }}>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{user?.name}</div>
                      <div style={{ fontSize: 11, color: 'var(--c-text-3)', marginTop: 2 }}>{user?.email}</div>
                    </div>
                    <button
                      onClick={() => { setUserMenuOpen(false); logout(); }}
                      style={{
                        width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                        padding: '12px 16px', background: 'transparent',
                        border: 'none', cursor: 'pointer',
                        color: 'var(--c-red)', fontSize: 13, fontWeight: 500,
                        fontFamily: "'Inter', sans-serif",
                        transition: 'background 0.2s',
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--c-surface-hover)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <LogOut size={14} /> Sign out
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main>
          {activePage === 'dashboard' && <Dashboard onNavigate={setActivePage} />}
          {activePage === 'analytics' && <Analytics />}
          {activePage === 'scraper' && <ScraperInterface />}
          {activePage === 'collector' && <LeadCollector />}
          {activePage === 'records' && <RecordsBrowser />}
          {activePage === 'email' && <EmailCampaign />}
          {activePage === 'inbox' && <InboxManager />}
          {activePage === 'followups' && <Followups />}
          {activePage === 'pipeline' && <LeadPipeline />}
          {activePage === 'demos' && <DemosTracker />}
          {activePage === 'mailboxes' && <Mailboxes />}
          {activePage === 'settings' && <Settings />}
        </main>
      </div>
    </div>
  );
};

const AppContent = () => {
  const { user, loading } = useAuth();
  const [authView, setAuthView] = useState('login');

  // Initialize theme class on mount (for unauthenticated pages too)
  useEffect(() => {
    const saved = localStorage.getItem('theme');
    const prefersDark = window.matchMedia?.('(prefers-color-scheme: dark)').matches;
    if (saved === 'dark' || (!saved && prefersDark)) {
      document.documentElement.classList.add('dark');
    }
  }, []);

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--c-bg)' }}>
        <div style={{ textAlign: 'center' }}>
          <div className="spinner" style={{ margin: '0 auto 16px' }} />
          <div style={{ color: 'var(--c-text-2)', fontSize: 14 }}>Loading WNLeads PRO...</div>
        </div>
      </div>
    );
  }

  if (!user) {
    return authView === 'login'
      ? <LoginPage onNavigate={() => setAuthView('register')} />
      : <RegisterPage onNavigate={() => setAuthView('login')} />;
  }

  return <AuthenticatedApp />;
};

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
